/** Microsoft Clarity client helpers (script is loaded in index.html). */

declare global {
  interface Window {
    clarity?: (...args: unknown[]) => void;
  }
}

/**
 * Custom tag key for the intake report id. Must match BFF Tracking deeplink
 * Variables=intakeSessionId:{reportId}. Avoids the ambiguous `sessionId` name
 * (easy to confuse with Clarity's own session id).
 */
export const CLARITY_SESSION_TAG = "intakeSessionId";

/** Ids already bound this tab (for one-shot debug events only). */
const boundIds = new Set<string>();

function clarityFn(): ((...args: unknown[]) => void) | undefined {
  const clarity = window.clarity;
  return typeof clarity === "function" ? clarity : undefined;
}

/**
 * Attach the intake session id (reportId) to Clarity via custom tag
 * `intakeSessionId`.
 *
 * Always re-applies a plain string tag. Clarity custom tags are tied to the
 * current page view; SPA navigations (wizard → preview) start a new page view,
 * so skipping a second set() after the first id leaves later recordings untagged.
 * Tracking URLs use Variables=intakeSessionId:{plainReportId}.
 */
export function setClaritySessionId(sessionId: string): void {
  if (!sessionId) return;

  const clarity = clarityFn();
  if (!clarity) return;

  // Plain string only (not string[] / Set). Re-set every call for SPA page views.
  clarity("set", CLARITY_SESSION_TAG, sessionId);

  // Friendly name in the player; Tracking deeplink uses the custom tag above.
  clarity("identify", sessionId, sessionId, sessionId, sessionId);

  if (!boundIds.has(sessionId)) {
    boundIds.add(sessionId);
    // Smart event marker for debugging in Filters / recording timeline.
    clarity("event", "intake_session_bound");
  }
}
